import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';

export interface MediaTag {
    id: number;
    mediaId: number;
    tag: string;
    source: 'capture' | 'action' | 'admin' | null;
    createdAt: string;
}

/**
 * Per-image tags (media_tag) — the free-form tags that scope tag-filtered
 * slideshows. Distinct from rekognition labels: tags are operational
 * ("slideshow_1", "team a") and are applied by booth capture stages,
 * setTag/unsetTag experience actions, or operators in the media manager.
 */
export default function useMediaTags() {
    const authenticatedApi = useAuthenticatedApi();

    const getTagsForMedia = async (mediaId: number) => {
        return await authenticatedApi.get<MediaTag[]>(
            `/media-tags/media/${mediaId}`,
        );
    };

    // Idempotent per (media, tag); returns the media's full tag list.
    const addTags = async (mediaId: number, tags: string[]) => {
        return await authenticatedApi.post<MediaTag[]>(
            `/media-tags/media/${mediaId}`,
            JSON.stringify({ tags }),
        );
    };

    // Returns the media's remaining tag list.
    const removeTags = async (mediaId: number, tags: string[]) => {
        return await authenticatedApi.delete<MediaTag[]>(
            `/media-tags/media/${mediaId}`,
            { data: { tags } },
        );
    };

    // Bulk tagging by share identifier (the shape bulk selection UIs already
    // have on hand). source 'admin' records these as operator-applied,
    // distinct from booth setTag actions.
    const addTagsByShareIdentifiers = async (
        shareIdentifiers: string[],
        tags: string[],
    ) => {
        return await authenticatedApi.post<{ added: number }>(
            '/media-tags/share-identifiers/add',
            JSON.stringify({ shareIdentifiers, tags, source: 'admin' }),
        );
    };

    const removeTagsByShareIdentifiers = async (
        shareIdentifiers: string[],
        tags: string[],
    ) => {
        return await authenticatedApi.post<{ removed: number }>(
            '/media-tags/share-identifiers/remove',
            JSON.stringify({ shareIdentifiers, tags }),
        );
    };

    return {
        getTagsForMedia,
        addTags,
        removeTags,
        addTagsByShareIdentifiers,
        removeTagsByShareIdentifiers,
    };
}
